import { Box, ButtonBase, Typography } from "@mui/material";
import logoLendia from "@assets/img/lendia/Logo.svg";
import { Button } from "./Button";
import { FinancieraLogo } from "./FinancieraLogo";

export interface OfertaStat {
  label: string;
  value: string;
  /** Aclaración de la cifra; se muestra como tooltip y subraya el valor con puntitos. */
  tooltip?: string;
}

export interface OfertaCardData {
  id: string;
  financiera: string;
  /** URL del logo. Si no hay (o falla la carga) se dibuja el nombre de la financiera. */
  logo?: string;
  /** `true` cuando la financiera formaliza dentro del entorno digital powered by Lendia. */
  digital: boolean;
  producto: string;
  conGarantia: boolean;
  /** Días de vigencia de la preoferta. Sin dato no se dibuja el bloque de vigencia. */
  vigenciaDias?: number;
  stats: OfertaStat[];
  notaTitulo: string;
  notas: string[];
}

interface Props {
  oferta: OfertaCardData;
  /** Bloquea las acciones mientras corre una llamada al API. */
  disabled: boolean;
  onElegir: () => void;
  onDescartar: () => void;
}

/** Tonos del diseño (`S_PreAprobacionMulti`) sin equivalente en la paleta del theme. */
const CARD_BORDER = "#E7EEF4";
const INNER_RULE = "#EEF3F8";
const STAT_LABEL = "#89A0B8";
const NOTA_TITLE = "#3B6FB0";
const NOTA_TEXT = "#5C6B7C";
const MUTED = "#7E90A4";
const DISCARD = "#8CA0B4";
const VIGENCIA_BG = "#F7FAFD";
const VIGENCIA_BORDER = "#EAF1F7";
const TOOLTIP_UNDERLINE = "#B9CBDD";
const BADGE_BORDER = "#E1EDF9";
const BADGE_DIVIDER = "#B9D3EE";
const BADGE_STRONG = "#2B60B8";

/** `100% DIGITAL · Powered by Lendia`: distingue a las financieras que formalizan vía Lendia. */
const DigitalBadge = () => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "7px",
      p: "5px 11px 5px 7px",
      borderRadius: "999px",
      background: "linear-gradient(90deg, #F4F9FE, #FAFCFF)",
      border: `1px solid ${BADGE_BORDER}`,
      boxShadow: "0 2px 6px rgba(82, 140, 214, 0.06)",
    }}
  >
    <Typography component="span" sx={{ fontWeight: 700, fontSize: 9.5, letterSpacing: "0.1em", color: BADGE_STRONG }}>
      100% DIGITAL
    </Typography>
    <Box sx={{ width: "1px", height: 11, bgcolor: BADGE_DIVIDER }} />
    <Typography
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        fontWeight: 500,
        fontSize: 9.5,
        color: NOTA_TITLE,
      }}
    >
      Powered by <Box component="img" src={logoLendia} alt="Lendia" sx={{ height: 10, display: "block" }} />
    </Typography>
  </Box>
);

/**
 * Tarjeta de preoferta del marketplace (`S_PreAprobacionMulti` del diseño): logo de la
 * financiera, vigencia, cuadro de condiciones, notas y acciones.
 *
 * No conoce el API ni el store: recibe la preoferta ya armada y avisa por callbacks.
 *
 * TODO: el diseño muestra además la fecha de emisión, los días restantes con barra de
 * progreso y el estado "Vencida" con su acción "Solicitar nueva oferta". La oferta del API
 * solo trae `days_validity` (número de días, sin fecha de emisión) y no hay endpoint para
 * resolicitar, así que por ahora solo se muestra la vigencia en días.
 */
export const OfertaCard = ({ oferta, disabled, onElegir, onDescartar }: Props) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      height: "100%",
      bgcolor: "background.paper",
      border: `1px solid ${CARD_BORDER}`,
      borderRadius: "20px",
      p: "26px 20px",
      boxShadow: "0 8px 28px rgba(6, 44, 78, 0.08)",
    }}
  >
    {/* Con badge el logo va más chico, para que el conjunto ocupe el mismo alto en toda la fila. */}
    {oferta.digital ? (
      <>
        <Box sx={{ height: 56, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <FinancieraLogo logo={oferta.logo} financiera={oferta.financiera} maxHeight={52} maxWidth="86%" />
        </Box>
        <Box sx={{ height: 30, display: "flex", alignItems: "center", justifyContent: "center", mt: "4px" }}>
          <DigitalBadge />
        </Box>
      </>
    ) : (
      <Box sx={{ height: 90, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <FinancieraLogo logo={oferta.logo} financiera={oferta.financiera} maxHeight={84} maxWidth="80%" />
      </Box>
    )}

    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        borderTop: `1px solid ${INNER_RULE}`,
        mt: "16px",
        pt: "18px",
      }}
    >
      <Typography
        sx={{
          minHeight: 34,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          fontWeight: 700,
          fontSize: 12,
          lineHeight: 1.3,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: "primary.accent",
          mb: "14px",
        }}
      >
        {oferta.conGarantia ? `${oferta.producto} · Con garantía` : oferta.producto}
      </Typography>

      {!!oferta.vigenciaDias && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: "10px",
            mb: "16px",
            p: "10px 12px",
            bgcolor: VIGENCIA_BG,
            border: `1px solid ${VIGENCIA_BORDER}`,
            borderRadius: "10px",
          }}
        >
          <Typography sx={{ fontSize: 10, color: STAT_LABEL }}>Vigencia</Typography>
          <Typography sx={{ fontWeight: 700, fontSize: 12.5, color: "primary.dark" }}>
            {oferta.vigenciaDias} día{oferta.vigenciaDias === 1 ? "" : "s"}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 14px", mb: "18px" }}>
        {oferta.stats.map((stat) => (
          <Box key={stat.label} sx={{ textAlign: "center" }}>
            <Typography sx={{ fontSize: 11, color: STAT_LABEL, mb: "4px" }}>{stat.label}</Typography>
            <Typography
              title={stat.tooltip}
              sx={{
                display: "inline-block",
                fontWeight: 600,
                fontSize: 14,
                color: "primary.dark",
                cursor: stat.tooltip ? "help" : "default",
                borderBottom: stat.tooltip ? `1px dotted ${TOOLTIP_UNDERLINE}` : "none",
              }}
            >
              {stat.value}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ pt: "18px", borderTop: `1px solid ${INNER_RULE}` }}>
        <Typography sx={{ fontWeight: 600, fontSize: 12.5, color: NOTA_TITLE, mb: "8px" }}>
          {oferta.notaTitulo}
        </Typography>
        {/* Alto fijo con scroll: mantiene alineadas las acciones de todas las tarjetas de la fila. */}
        <Box
          component="ul"
          sx={{
            listStyle: "none",
            m: 0,
            p: 0,
            height: 120,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          {oferta.notas.map((nota) => (
            <Box component="li" key={nota} sx={{ display: "flex", gap: "8px" }}>
              <Typography component="span" sx={{ fontSize: 12.5, lineHeight: 1.45, color: MUTED }}>
                •
              </Typography>
              <Typography sx={{ fontSize: 12.5, lineHeight: 1.45, color: NOTA_TEXT }}>{nota}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Button
        tone="accent"
        fullWidth
        disabled={disabled}
        onClick={onElegir}
        sx={{ mt: "20px", p: "13px", fontSize: 14.5, boxShadow: "0 6px 16px rgba(255, 157, 20, 0.28)" }}
        data-testid={`ofertas-${oferta.id}-elegir-button`}
      >
        Elegir preoferta
      </Button>

      <ButtonBase
        onClick={onDescartar}
        disabled={disabled}
        data-testid={`ofertas-${oferta.id}-descartar-button`}
        sx={{
          mt: "10px",
          width: "100%",
          p: "4px",
          fontWeight: 600,
          fontSize: 12.5,
          color: DISCARD,
          textDecoration: "underline",
          textUnderlineOffset: 3,
          transition: "color .15s",
          "&:hover": { color: NOTA_TEXT },
          "&.Mui-disabled": { opacity: 0.5 },
        }}
      >
        No me interesa
      </ButtonBase>
    </Box>
  </Box>
);
